import { useTranslation } from 'react-i18next'

import { Checkbox, Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { formatMoney } from '@/lib/format'

/* The tax block both invoice surfaces share — and, once, the sheets around it.
 *
 * TWO SHEETS USED TO LIVE HERE, and both are worth a paragraph so nobody
 * reintroduces either.
 *
 * `NewInvoiceSheet` raised the draft: a currency, two dates, a reference and
 * the tax block, submitted as one `NewInvoicePayload`. By the time it was
 * removed no screen opened it, so it was a form with no door quietly diverging
 * from the one that was actually used. Raising is now `useRaiseInvoiceDraft`
 * and asks nothing at all.
 *
 * `InvoiceEditorSheet` amended the draft: the header form, the charges editor
 * and the read-only facts, in a 440px column over the documents table. It went
 * for a defect it could not be patched out of cleanly. `PUT
 * /cases/:id/invoices/:invoiceId` is a WHOLE-HEADER replace and
 * `invoiceBody.change()` reads an absent `trancheId` as an instruction to
 * UNLINK the instalment — the invoice handler suite pins exactly that, in
 * `TestOmittingTheTrancheIdOnTheHeaderPutUnlinksTheInstalment` — and this
 * sheet's form had no tranche field at all.
 * So every correction of an invoice date made from the case's document table
 * silently detached the invoice from the payment it was billing, and the case
 * went back to reporting the same money owed twice. Adding the field would have
 * produced a second, narrower copy of the invoice page's header form; echoing
 * the stored value back would have produced a control that cannot move a link a
 * draft may legitimately move. The invoice's workplace is a PAGE now
 * (`routes/CaseInvoicePage.tsx`): a document printed on A4 is read across, not
 * down a column, and the page asks the question properly.
 *
 * What is left is the one block those surfaces genuinely shared.
 */

/** The FR-10 tax block: an optional line that prints only when it applies.
 *
 *  The rate field appears with the tick rather than sitting there greyed out,
 *  because a rate on an untaxed invoice is not a disabled field — it is a
 *  figure that does not exist.
 *
 *  EXPORTED, because the invoice page asks the same two questions and the PUT
 *  behind both is a whole-header replace — a second copy of this block would be
 *  a second chance for one of them to stop sending `vatRate` and silently clear
 *  the tax on save. */
export function VatBlock({
  applies,
  rate,
  rateError,
  amount,
  currency,
  onChange,
}: {
  applies: boolean
  rate: string
  rateError?: string
  /** The tax the server computed, when there is one to show. */
  amount?: string | null
  currency?: string
  onChange: (change: { vatApplies: boolean; vatRate: string }) => void
}) {
  const { t } = useTranslation()

  return (
    <div className="flex flex-col gap-2.5">
      <Checkbox
        label={t('finance.invoices.vatApplies')}
        hint={t('finance.invoices.vatHint')}
        checked={applies}
        onCheckedChange={(checked) => onChange({ vatApplies: checked, vatRate: rate })}
      />

      {applies ? (
        <Field
          label={t('finance.invoices.vatRate')}
          required
          error={rateError}
          hint={
            amount
              ? t('finance.invoices.vatAmount', {
                  amount: formatMoney(amount, currency ?? 'USD'),
                })
              : t('finance.invoices.vatRateHint')
          }
          className="w-40"
        >
          {(props) => (
            <Input
              inputMode="decimal"
              value={rate}
              onChange={(e) => onChange({ vatApplies: applies, vatRate: e.target.value })}
              className="asa-num"
              {...props}
            />
          )}
        </Field>
      ) : null}
    </div>
  )
}
